import styled from '@emotion/styled'
import FormControlLabel from '@mui/material/FormControlLabel'
import Icon from '@mui/material/Icon'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'
import React from 'react'

import CountryFlag from '@/app/component/atom/flag'
import Autocomplete from '@/app/component/atom/form/autocomplete'
import IconText from '@/app/component/layout/icon-text'

type CountryValue = {
  input: string
  code?: string
}

type CountriesList = Record<string, string>

type Props = {
  countryValue: CountryValue
  countryCodeError: string
  countriesList: CountriesList
  countriesOption: 'single' | 'multi'
  onCountryOptionChange: (payload: { value: 'single' | 'multi'; error?: string }) => void
  onCountryCodeChange: (payload: { name?: string; value: CountryValue; error?: string }) => void
}

const getCountries = (countriesList: CountriesList) =>
  Object.keys(countriesList).map((name) => ({
    label: name,
    value: name,
    component: () => (
      <Typography>
        <CountryFlag code={countriesList[name]} />
        &nbsp;{name}
      </Typography>
    ),
  }))

const ContactsUploadCountriesContent: React.FC<Props> = ({
  countryValue,
  countryCodeError,
  countriesList,
  countriesOption,
  onCountryOptionChange,
  onCountryCodeChange,
}) => {
  const countries = React.useMemo(() => getCountries(countriesList), [countriesList])

  return (
    <div>
      <SectionIntro>The phone numbers in the file:</SectionIntro>
      <CountryOptionsContainer>
        <CountryOption
          label={
            <OptionLabel>
              <OptionTitle>
                are all from the <b>same</b> country.
              </OptionTitle>
              <OptionDescription>
                You may use numbers in national format (e.g. <b>09212345678</b>) or international format (e.g. for the
                Philippines <b>639212345678</b>). You can use both formats in the same file.
              </OptionDescription>
            </OptionLabel>
          }
          value="single"
          onChange={(e) => {
            e.preventDefault()
            onCountryOptionChange({ value: (e.target as HTMLInputElement).value as 'single' | 'multi' })
          }}
          control={<Radio checked={countriesOption === 'single'} color="primary" />}
        />
        {countriesOption === 'single' && (
          <SelectContainer>
            <Autocomplete
              name="uploads-country"
              label="Select country"
              editable={true}
              restricted={true}
              error={countryCodeError}
              values={countries}
              value={countryValue.input}
              onChange={({ name, value, error = '' }: { name: string; value: string; error?: string }) => {
                onCountryCodeChange({
                  name,
                  error,
                  value: {
                    input: value,
                    code: countriesList[value],
                  },
                })
              }}
              onBlur={({ name, value, error = '' }: { name: string; value: string; error?: string }) => {
                onCountryCodeChange({
                  name,
                  error,
                  value: {
                    input: value,
                    code: countriesList[value],
                  },
                })
              }}
              onOpen={() => undefined}
              onClose={() => undefined}
            />
          </SelectContainer>
        )}
        <CountryOption
          label={
            <OptionLabel>
              <OptionTitle>
                may be from <b>different</b> countries.
              </OptionTitle>
              <OptionDescription>
                Your numbers <b>must</b> be in international format and start with the country code (e.g. Philippines{' '}
                <b>639212345678</b>).
              </OptionDescription>
            </OptionLabel>
          }
          value="multi"
          onChange={(e) => {
            e.preventDefault()
            onCountryOptionChange({ value: (e.target as HTMLInputElement).value as 'single' | 'multi' })
          }}
          control={<Radio checked={countriesOption === 'multi'} color="primary" />}
        />
      </CountryOptionsContainer>
      <InfoWrap>
        <Icon color="primary">info</Icon>
        <InfoText>
          Phone numbers may include special characters and spaces, so you can write the same number for example as{' '}
          <b>(0063)921-234-5678</b>  or <b>+63 921 234 5678</b>.
        </InfoText>
      </InfoWrap>
    </div>
  )
}

const CountryOptionsContainer = styled('div')({
  display: 'grid',
  gap: '0.75rem',
  marginBottom: '0.5rem',
})

const CountryOption = styled(FormControlLabel)({
  alignItems: 'flex-start',
  margin: 0,
  borderRadius: '0.5rem',
  padding: '0.375rem 0.5rem',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.06)',
  },
  '.MuiRadio-root': {
    marginTop: -1,
    paddingBottom: 0,
    paddingTop: 0,
  },
  '.MuiFormControlLabel-label': {
    display: 'block',
  },
})

const OptionLabel = styled('div')({
  display: 'grid',
  gap: '0.2rem',
})

const OptionTitle = styled(Typography)({
  fontSize: '0.95rem',
  lineHeight: 1.35,
})

const OptionDescription = styled(Typography)({
  color: 'rgba(0, 0, 0, 0.72)',
  fontSize: '0.95rem',
  lineHeight: 1.45,
})

const SelectContainer = styled('div')({
  padding: '0 8px 0 50px',
})

const SectionIntro = styled(Typography)({
  fontSize: '0.95rem',
  fontWeight: 600,
  marginBottom: '0.5rem',
})

const InfoWrap = styled(IconText)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.6rem',
  marginTop: '0.25rem',
  '.MuiIcon-root': {
    marginTop: '0.15rem',
  },
})

const InfoText = styled(Typography)({
  color: 'rgba(0, 0, 0, 0.72)',
  fontSize: '0.95rem',
  lineHeight: 1.45,
})

export default ContactsUploadCountriesContent
